import { useMemo, useState, type UIEvent } from "react";

import type { components } from "../../api/schema";
import { formatFillAction } from "../../lib/tradeFormat";

type PreviewedFillResponse = components["schemas"]["PreviewedFillResponse"];
type FillAction = components["schemas"]["FillAction"];

type SortColumn =
  | "executed_at"
  | "symbol"
  | "action"
  | "quantity"
  | "price"
  | "external_id"
  | "is_duplicate";
type SortDirection = "asc" | "desc";
type SideFilter = "all" | "buys" | "sells";
type StatusFilter = "all" | "new" | "duplicate";

type ParsedRowsTableProps = {
  rows: ReadonlyArray<PreviewedFillResponse>;
};

const ROW_HEIGHT = 40;
const VIEWPORT_MAX_HEIGHT = 480;
const VIEWPORT_VISIBLE_ROWS = Math.ceil(VIEWPORT_MAX_HEIGHT / ROW_HEIGHT);
const OVERSCAN = 5;
const BUY_ACTIONS: ReadonlySet<FillAction> = new Set<FillAction>(["bto", "btc"]);
const SELL_ACTIONS: ReadonlySet<FillAction> = new Set<FillAction>(["sto", "stc"]);

function formatExecutedAt(value: string): string {
  return new Date(value).toLocaleString();
}

function compareStrings(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

function compareNumeric(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  if (Number.isNaN(na) || Number.isNaN(nb)) return compareStrings(a, b);
  return na - nb;
}

function rowCompareFor(column: SortColumn) {
  return (a: PreviewedFillResponse, b: PreviewedFillResponse): number => {
    switch (column) {
      case "executed_at":
        return compareStrings(a.exec_time_utc, b.exec_time_utc);
      case "symbol":
        return compareStrings(a.symbol, b.symbol);
      case "action":
        return compareStrings(a.action, b.action);
      case "quantity":
        return compareNumeric(a.quantity, b.quantity);
      case "price":
        return compareNumeric(a.price_amount, b.price_amount);
      case "external_id":
        return compareStrings(a.external_id, b.external_id);
      case "is_duplicate":
        return Number(a.is_duplicate) - Number(b.is_duplicate);
    }
  };
}

export function ParsedRowsTable({ rows }: ParsedRowsTableProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn>("executed_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [symbolFilter, setSymbolFilter] = useState("");
  const [sideFilter, setSideFilter] = useState<SideFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [scrollTop, setScrollTop] = useState(0);

  const filteredRows = useMemo(() => {
    const symbol = symbolFilter.trim().toLowerCase();
    return rows.filter((row) => {
      if (symbol !== "" && !row.symbol.toLowerCase().includes(symbol)) return false;
      if (sideFilter === "buys" && !BUY_ACTIONS.has(row.action)) return false;
      if (sideFilter === "sells" && !SELL_ACTIONS.has(row.action)) return false;
      if (statusFilter === "new" && row.is_duplicate) return false;
      if (statusFilter === "duplicate" && !row.is_duplicate) return false;
      return true;
    });
  }, [rows, symbolFilter, sideFilter, statusFilter]);

  const sortedRows = useMemo(() => {
    const compare = rowCompareFor(sortColumn);
    const sign = sortDirection === "asc" ? 1 : -1;
    return filteredRows.slice().sort((a, b) => sign * compare(a, b));
  }, [filteredRows, sortColumn, sortDirection]);

  function handleSort(column: SortColumn) {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  }

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    setScrollTop(event.currentTarget.scrollTop);
  }

  const total = sortedRows.length;
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(total, startIndex + VIEWPORT_VISIBLE_ROWS + OVERSCAN * 2);
  const visibleRows = sortedRows.slice(startIndex, endIndex);
  const topSpacer = startIndex * ROW_HEIGHT;
  const bottomSpacer = (total - endIndex) * ROW_HEIGHT;

  return (
    <div className="space-y-3" data-testid="parsed-rows-table">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs text-on-surface-variant">
          Symbol contains
          <input
            type="text"
            value={symbolFilter}
            onChange={(e) => setSymbolFilter(e.target.value)}
            placeholder="Filter symbol…"
            aria-label="Filter symbol"
            className="h-8 rounded-control border border-outline bg-surface-container px-2 text-sm text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-on-surface-variant">
          Side
          <select
            value={sideFilter}
            onChange={(e) => setSideFilter(e.target.value as SideFilter)}
            aria-label="Filter side"
            className="h-8 rounded-control border border-outline bg-surface-container px-2 text-sm text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
          >
            <option value="all">All sides</option>
            <option value="buys">Buys (Buy + Cover)</option>
            <option value="sells">Sells (Short + Sell)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-on-surface-variant">
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            aria-label="Filter status"
            className="h-8 rounded-control border border-outline bg-surface-container px-2 text-sm text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
          >
            <option value="all">All rows</option>
            <option value="new">New only</option>
            <option value="duplicate">Duplicate only</option>
          </select>
        </label>
      </div>

      <p
        className="text-xs text-on-surface-variant"
        data-testid="parsed-rows-filter-summary"
      >
        Showing {total} of {rows.length} row(s)
      </p>

      <div
        className="overflow-y-auto rounded-control border border-outline-variant"
        style={{ maxHeight: VIEWPORT_MAX_HEIGHT }}
        onScroll={handleScroll}
        data-testid="parsed-rows-viewport"
      >
        <table className="w-full text-left text-sm text-on-surface">
          <thead className="sticky top-0 z-10 bg-surface-container text-xs uppercase text-on-surface-variant">
            <tr className="border-b border-outline">
              <SortableHeader
                column="executed_at"
                label="Executed"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                column="symbol"
                label="Symbol"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                column="action"
                label="Action"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                column="quantity"
                label="Qty"
                align="right"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                column="price"
                label="Price"
                align="right"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                column="external_id"
                label="External id"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                column="is_duplicate"
                label="Status"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody>
            {topSpacer > 0 && (
              <tr aria-hidden="true" style={{ height: topSpacer }}>
                <td colSpan={7} />
              </tr>
            )}
            {total === 0 && (
              <tr data-testid="parsed-rows-empty">
                <td
                  colSpan={7}
                  className="px-2 py-6 text-center text-sm text-on-surface-variant"
                >
                  No rows match the current filters.
                </td>
              </tr>
            )}
            {visibleRows.map((row) => (
              <tr
                key={row.external_id}
                className="border-b border-outline-variant last:border-0"
                style={{ height: ROW_HEIGHT }}
                data-testid={`parsed-row-${row.external_id}`}
              >
                <td className="px-2 py-2 text-xs">
                  {formatExecutedAt(row.exec_time_utc)}
                </td>
                <td className="px-2 py-2 font-mono">{row.symbol}</td>
                <td className="px-2 py-2">{formatFillAction(row.action)}</td>
                <td className="px-2 py-2 text-right">{row.quantity}</td>
                <td className="px-2 py-2 text-right">{row.price_amount}</td>
                <td
                  className="max-w-[14rem] truncate px-2 py-2 font-mono text-xs"
                  title={row.external_id}
                >
                  {row.external_id}
                </td>
                <td className="px-2 py-2">
                  {row.is_duplicate ? (
                    <span
                      className="rounded-full bg-warning/20 px-2 py-0.5 text-xs font-medium text-warning"
                      data-testid="row-status-duplicate"
                    >
                      Duplicate — will skip
                    </span>
                  ) : (
                    <span
                      className="rounded-full bg-success/20 px-2 py-0.5 text-xs font-medium text-success"
                      data-testid="row-status-new"
                    >
                      New
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {bottomSpacer > 0 && (
              <tr aria-hidden="true" style={{ height: bottomSpacer }}>
                <td colSpan={7} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type SortableHeaderProps = {
  column: SortColumn;
  label: string;
  align?: "left" | "right";
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn) => void;
};

function SortableHeader({
  column,
  label,
  align = "left",
  sortColumn,
  sortDirection,
  onSort,
}: SortableHeaderProps) {
  const active = sortColumn === column;
  const ariaSort: "ascending" | "descending" | "none" = active
    ? sortDirection === "asc"
      ? "ascending"
      : "descending"
    : "none";
  const indicator = active ? (sortDirection === "asc" ? " ▲" : " ▼") : "";
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={align === "right" ? "px-2 py-2 text-right" : "px-2 py-2"}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 font-medium uppercase hover:text-on-surface focus-visible:outline-none focus-visible:underline"
        data-testid={`parsed-rows-sort-${column}`}
      >
        {label}
        <span aria-hidden="true">{indicator}</span>
      </button>
    </th>
  );
}
